import {
  CalculatedCostBuy,
  CalculatedCostSell,
  FuturesCalculatedCost,
  getFormatter,
  InstrumentCategoryEnum,
  isDefined,
  Loadable,
  LoadableState,
  TradeType,
} from '@eih-netbroker/common';
import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import { useLocale } from '../../../../../context/NbLocalizationContext';
import { NbIndefiniteLoaderValueWrapper } from '../../../../../shared/components/loader-value/NbIndefiniteLoaderValueWrapper';
import { ValidatedOrder } from '../../../services/order/useNormalOrderView';
import { useOrderConfirmCalculateCost } from '../../../services/order/useOrderConfirmCalculateCost';
import { useOrderState } from '../../OrderStateContext';
import { OrderConfirmBlock, OrderConfirmRow } from './OrderConfirmComponents';
import classes from './OrderConfirmCostBlock.module.scss';

type OrderConfirmCostBlockProps = {
  orderForm: ValidatedOrder;
};

type CalculatedCost = (CalculatedCostBuy & { commission: number }) | (CalculatedCostSell & { commission: number });

export const OrderConfirmCostBlock = ({ orderForm }: OrderConfirmCostBlockProps) => {
  const { calculateOrderCost, resetCostCalculation } = useOrderConfirmCalculateCost();
  const [totalPrice, setTotalPrice] = useState<LoadableState<number>>(Loadable.uninitialized);
  const [futuresPrice, setFuturesPrice] = useState<LoadableState<FuturesCalculatedCost>>(Loadable.uninitialized);
  const { translation } = useLocale();
  const { formatCurrency } = getFormatter();
  const { calculatedCostState, getOrderTotalPrice, getFuturesOrderTransactionInfo } = useOrderState();
  const t = translation.newOrderConfirm;

  const calculatedCost = useMemo<LoadableState<CalculatedCost>>(() => {
    if (!isDefined(calculatedCostState)) {
      return Loadable.uninitialized;
    }

    return Loadable.map(calculatedCostState, calculatedCostLoaded => {
      if (calculatedCostLoaded.calculatedCost.tradeType === TradeType.Buy) {
        return {
          ...calculatedCostLoaded.calculatedCost,
          commission: calculatedCostLoaded.calculatedCost.buyAmount,
        };
      } else if (calculatedCostLoaded.calculatedCost.tradeType === TradeType.Sell) {
        return {
          ...calculatedCostLoaded.calculatedCost,
          commission: calculatedCostLoaded.calculatedCost.sellAmount,
        };
      }
    });
  }, [calculatedCostState]);

  const transactionSum = useMemo<LoadableState<number>>(() => {
    if (orderForm.instrument.instrumentCategory === InstrumentCategoryEnum.Futures) {
      return Loadable.merge([calculatedCost, futuresPrice], (calculatedCost, futuresPrice) => {
        const signedCommission =
          orderForm.tradeType === TradeType.Buy ? calculatedCost.commission : -calculatedCost.commission;
        return futuresPrice.netValueSum + signedCommission;
      });
    } else {
      return Loadable.merge([calculatedCost, totalPrice], (calculatedCost, totalPrice) => {
        const signedCommission =
          orderForm.tradeType === TradeType.Buy ? calculatedCost.commission : -calculatedCost.commission;
        return totalPrice.value + signedCommission;
      });
    }
  }, [calculatedCost, futuresPrice, orderForm.instrument.instrumentCategory, orderForm.tradeType, totalPrice]);

  const initFuturesPrices = useEffectEvent(async () => {
    if (orderForm.instrument.instrumentCategory !== InstrumentCategoryEnum.Futures) {
      return;
    }

    setFuturesPrice(Loadable.loading);
    try {
      const futuresPrice = await getFuturesOrderTransactionInfo(orderForm);
      setFuturesPrice(Loadable.loaded(futuresPrice));
    } catch {
      setFuturesPrice(Loadable.failed);
    }
  });

  const initTotalPrice = useEffectEvent(async () => {
    if (orderForm.instrument.instrumentCategory === InstrumentCategoryEnum.Futures) {
      return;
    }

    setTotalPrice(Loadable.loading);
    try {
      const value = await getOrderTotalPrice(orderForm);
      setTotalPrice(Loadable.loaded(value));
    } catch {
      setTotalPrice(Loadable.failed);
    }
  });

  const initCalculateCost = useEffectEvent(async () => {
    if (orderForm && orderForm.instrument) {
      calculateOrderCost(orderForm.instrument, orderForm);
    } else {
      resetCostCalculation();
    }
  });

  useEffect(() => {
    initCalculateCost();
    initTotalPrice();
    initFuturesPrices();
  }, [orderForm]);

  return (
    <OrderConfirmBlock>
      {orderForm.instrument.instrumentCategory === InstrumentCategoryEnum.Futures && (
        <OrderConfirmRow
          label={t.netValue}
          testIdBase="order-confirm-net-value"
          value={
            <NbIndefiniteLoaderValueWrapper
              loadableValue={futuresPrice}
              loadedValueCb={price => <>{formatCurrency(price.netValueSum, orderForm.instrument.currency)}</>}
              errorComponent={<span>—</span>}
            />
          }
        />
      )}
      {orderForm.instrument.instrumentCategory !== InstrumentCategoryEnum.Futures && (
        <OrderConfirmRow
          label={t.orderValue}
          testIdBase="order-confirm-order-value"
          value={
            <NbIndefiniteLoaderValueWrapper
              loadableValue={totalPrice}
              loadedValueCb={price => <>{formatCurrency(price.value, orderForm.instrument.currency)}</>}
              errorComponent={<span>—</span>}
            />
          }
        />
      )}
      <OrderConfirmRow
        label={t.commission}
        testIdBase="order-confirm-commission"
        value={
          <NbIndefiniteLoaderValueWrapper
            loadableValue={calculatedCost}
            loadedValueCb={calculatedCost => <>{formatCurrency(calculatedCost.commission, calculatedCost.currency)}</>}
            errorComponent={<span>—</span>}
          />
        }
      />
      <div className={classes.divider} />
      <div
        className={classes.rowContainer}
        data-testid="order-confirm-total-transaction-value"
      >
        <span
          data-testid="order-confirm-total-transaction-value-label"
          className={classes.transactionLabel}
        >
          {t.totalTransactionValue}
        </span>
        <NbIndefiniteLoaderValueWrapper
          loadableValue={transactionSum}
          loadedValueCb={transactionSum => (
            <span
              data-testid="order-confirm-total-transaction-value"
              className={classes.transactionValue}
            >
              {formatCurrency(transactionSum.value, orderForm.instrument.currency)}
            </span>
          )}
          errorComponent={<span className={classes.transactionValue}>—</span>}
        />
      </div>
      {orderForm.instrument.instrumentCategory === InstrumentCategoryEnum.Futures && (
        <>
          <div className={classes.divider} />
          <OrderConfirmRow
            label={t.futuresMarginRequirement}
            testIdBase="order-confirm-futures-margin-requirement"
            value={
              <NbIndefiniteLoaderValueWrapper
                loadableValue={futuresPrice}
                loadedValueCb={futuresPrice => (
                  <>{formatCurrency(futuresPrice.marginRequirementSum, orderForm.instrument.currency)}</>
                )}
                errorComponent={<span>—</span>}
              />
            }
          />
        </>
      )}
    </OrderConfirmBlock>
  );
};
